import _, { startCase } from 'lodash';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import {
    CAMERA_PERMISSION,
    COMPANY_ACCESS_CODES,
    CUSTOM_FLOW_COMPANY_UUIDS,
    COUNTRY_USA_VALIDATION_VARIANTS,
    DOCUMENT_ERROR_MESSAGES,
    fakeUserInfo,
    ID_AUTHENTICATION_TYPES,
    INSTRUCTION_TYPES,
    QUICK_VERIFY_PROPERTY,
    REAUTHENTICATE_ROUTE,
    SELFIE_FLOW_TYPES,
    SSN_AND_ADDRESS_FIELDS_BLOCKED_CUSTOMERS,
} from './constants';
import { TWO_FA_ERRORS, TWO_FA_OTP_TYPE } from 'features/preIdentity/helper';
import { allAttemptsUsedVariants } from './constants/allAttemptsUsed';
import { ORDERED_FIELDS, DYNAMIC_LABEL_MAP, REMOVED_KEYS } from 'features/govtId/helper/constants';

import {
    isChrome,
    isSafari,
    isAndroid,
    isIOS,
    isMacOs,
    browserVersion,
    isSamsungBrowser,
    browserName,
} from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import { VERIFICATION_TYPES } from './constants/verifications';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { getCountryCodeByName } from './constants/country';
import { formatTimestamp, isDateInPast, getDobAgeYears, dateEighteenYearsAgo } from 'lib/date';
import { AUTH_DB_VERIFICATION_DECISION } from 'features/preIdentity/helper/constants';
import { Country, State } from 'country-state-city';
import states from './constants/states';
import { isVerificationCompleted } from '../lib/navigation';

export const toCamelCase = (snake_case_object) => {
    const camelCaseObject = {};
    _.forEach(snake_case_object, function (value, key) {
        if (_.isArray(value)) {
            value = value.map((v) => {
                // Only convert if the item is a plain object
                return _.isPlainObject(v) ? toCamelCase(v) : v;
            });
        } else if (_.isPlainObject(value)) {
            value = toCamelCase(value);
        }
        camelCaseObject[_.camelCase(key)] = value;
    });
    return camelCaseObject;
};

export const getErrorMessage = (error) => {
    if (error?.response?.data) {
        const errorObject =
            error.response.data.message ||
            error.response.data.errorMsg ||
            error.response.data.errorMessage ||
            error?.data?.errorMessage ||
            'Something went wrong.';

        switch (typeof errorObject) {
            case 'object':
                return errorObject.errorMessage || JSON.stringify(errorObject);
            case 'string':
                return errorObject;
            default:
                return 'Something went wrong.';
        }
    }
    return 'Something went wrong.';
};

export const setButtonVariant = (themeButtonVariant) => {
    return themeButtonVariant === 'outlined' ? 'outline-primary' : 'primary';
};

export const parseArray = (value) => {
    try {
        if (!value) return [];

        let parseValue = value;

        if (`${value}`.includes(':')) {
            const splitArrayFromObject = value.split(':');
            parseValue = splitArrayFromObject?.length ? splitArrayFromObject[1] : '[]';
        }

        return JSON.parse(parseValue);
    } catch (error) {
        return [];
    }
};

export const removeSpecialCharacters = (value) => {
    const string = value.replace(/[^a-zA-Z ]/g, '');
    return string.trim();
};

export const extractAcuantResult = ({
    requiredObjectKeys = [],
    listOfResults = [],
    specificIdentifierKey = 'Name',
    specificValueKey = 'Disposition', //Key of the specific property value want to access from acuant object
    terminateLoopMatchFound = false,
}) => {
    const resultLists = [];
    for (const object of listOfResults) {
        if (requiredObjectKeys.includes(object?.[specificIdentifierKey])) {
            resultLists.push(object?.[specificValueKey]);

            if (terminateLoopMatchFound) break;
        }
    }

    return resultLists;
};

export const ignoreFalsyValues = (props) => {
    return Object.fromEntries(
        Object.entries(props).filter(([key, value]) => !['', null, undefined].includes(value))
    );
};

export const verifyCountry = (allowedCountries, userCountry) => {
    if (!allowedCountries || !userCountry) return;

    if (allowedCountries === 'all') return;

    const countries = parseArray(allowedCountries);

    if (!countries?.includes(userCountry)) {
        return true; //country mismatch
    }
};

// TODO: this functionality is added in feature/bank/lib, not used anywhere now, remove this later
// functionality to redirect to success and error screen based on Finicity result
export const handleResultScreenRedirect = async ({
    profile,
    verificationConfiguration,
    lastPoint,
    navigate,
}) => {
    const { company, workflow } = profile;

    const bothEitherMethod =
        verificationConfiguration === 'AND' || verificationConfiguration === 'OR';
    const singleMethod =
        verificationConfiguration === 'SINGLE' && workflow?.idVerifications?.bank?.enabled;
    const bankAttemptsUsed = lastPoint?.data?.attempts >= workflow?.idVerifications?.bank?.attempts;
    const isDocumentVerifiedForThis = workflow?.idVerifications?.govtId?.enabled
        ? company?.user?.verificationDetails?.isDocumentValid
        : null;

    const currentKbaAttempts = company?.user?.verificationDetails?.kbaAttempts;
    const totalKbaAttempts = profile?.workflow?.idVerifications?.kba?.attempts;
    const isKbaAttemptsUsed = currentKbaAttempts >= totalKbaAttempts;
    const isKbaVerified = company?.user?.verificationDetails?.isKbaVerified === 1;
    const isKbaAvailable = !isKbaVerified && !isKbaAttemptsUsed;
    const isKBAVerifiedForThis = workflow?.idVerifications?.kba?.enabled ? isKbaAvailable : null;

    const allBankAttemptsUsed = () => {
        if (bankAttemptsUsed) {
            navigate('/verification/bank/failed');
        } else {
            toast.error('Bank account details don’t match your details.');
        }
    };

    if (!lastPoint || lastPoint?.data?.is_owner_matched) return null;

    if (bothEitherMethod && (isDocumentVerifiedForThis || isKBAVerifiedForThis)) {
        toast.error('Bank account details don’t match your details. Please try again.');
    } else if (bothEitherMethod && !isDocumentVerifiedForThis && !isKBAVerifiedForThis) {
        allBankAttemptsUsed();
    } else if (singleMethod) {
        allBankAttemptsUsed();
    }
};

export const minimalToaster = (message) =>
    toast(message, {
        theme: 'dark',
        hideProgressBar: true,
    });

export const convertSingleToDoubleDigit = (num) => {
    return num.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
};

export const isReAuthenticateRoute = (pathname) => {
    return pathname?.includes(REAUTHENTICATE_ROUTE);
};

//check ID front/back sides have expired date
const anyOfIdSidesExpired = ({ acuantResponse }) => {
    let expired = false;

    try {
        // Acuant generates an array of objects named Fields, encompassing all details related to the user-uploaded document.
        // Within this array, we aim to gather objects associated with the expiration date.
        // Such objects have a property named "DataFieldReferences", housing IDs of expiration date objects from both document sides (front and back).
        // By examining the expiration date values within these objects, we can determine whether any side of the document exhibits an expired date.

        //collect expiration date object IDs of front and back sides of document
        const expireDateDataFieldIds = extractAcuantResult({
            specificValueKey: 'DataFieldReferences',
            requiredObjectKeys: ['Expiration Date'],
            listOfResults: acuantResponse.Fields,
            terminateLoopMatchFound: true,
        });

        //Collect expire dates belong to expiration object IDs
        const expirationDates = extractAcuantResult({
            specificValueKey: 'Value',
            specificIdentifierKey: 'Id',
            requiredObjectKeys: expireDateDataFieldIds[0],
            listOfResults: acuantResponse.DataFields,
        });

        expirationDates?.forEach((date) => {
            const expireDate = parseAcuantDateString(date);

            if (isDateInPast(expireDate)) {
                expired = true;
            }
        });
    } catch (error) {
        datadogRum.addError(
            new Error('DocumentError: Failed to check expiration dates of the document'),
            {
                acuantSdkError: false,
                extra: { error },
            }
        );
    }

    return expired;
};

const getAcuantPropertiesForLogging = (acuantResponse) => {
    try {
        const { Alerts, Result, Images } = acuantResponse ?? {};

        const data = {
            status: Result,
            alerts: Alerts,
        };

        if (Images?.length) {
            data.documentImages = Images.map((document) => {
                const properties = {
                    ...document,
                };

                delete properties?.Uri;

                return properties;
            });
        }

        return data;
    } catch (error) {}
};

export const parseAcuantDateString = (date) => {
    if (!date) return null;

    return formatTimestamp(
        new Date(Number.parseInt(date.substring(date.indexOf('(') + 1, date.indexOf(')') + 1))),
        'yyyy-MM-dd'
    );
};

export const checkIdErrorStatus = ({ authenticationType, acuantResponse }) => {
    const result = {
        message: [],
        properties: getAcuantPropertiesForLogging(acuantResponse) ?? {},
    };

    const { EXPIRED, EXPIRE_DATE_MISMATCH, EXPIRE_DATE_EMPTY } = DOCUMENT_ERROR_MESSAGES;

    const { ATTENTION } = ID_AUTHENTICATION_TYPES;

    const errors = extractAcuantResult({
        listOfResults: acuantResponse?.Alerts,
        requiredObjectKeys: ['Document Expired', 'Expiration Date Crosscheck'],
    });

    const expirationDate = parseAcuantDateString(acuantResponse?.Biographic?.ExpirationDate);

    const expiredDocument = isDateInPast(expirationDate);

    const expireDateMismatchError = errors?.includes(EXPIRE_DATE_MISMATCH);

    const idSidesExpired = anyOfIdSidesExpired({ acuantResponse });

    if (!expirationDate && authenticationType === ATTENTION) {
        result.isDefaultError = true;
        result.message = [EXPIRE_DATE_EMPTY];

        return result;
    }

    if (authenticationType === ATTENTION && expiredDocument) {
        result.isExpiredError = true;
        result.message = [EXPIRED];

        return result;
    }

    //user uploaded expired front and valid back images, or vice versa
    if (expireDateMismatchError && idSidesExpired) {
        result.isExpiredError = true;
        result.message = [EXPIRED];
    }

    return result;
};

export const isCustomFlowCompany = (companyUUID) => CUSTOM_FLOW_COMPANY_UUIDS.includes(companyUUID);

export const modifyThankYouPageContent = ({
    companyUUID,
    companyName,
    isQuickVerifyPassed = null,
}) => {
    const result = {
        description: '',
        defaultText: 'You’re all done!',
        defaultBtnText: `Return To ${companyName}`,
        headerText: '',
    };

    if (isQuickVerifyPassed) {
        result.description = 'You’re all done!';
        result.defaultText = 'Your identity has been verified successfully.';
        result.headerText = 'Identity Verification Passed';
    }

    if (isCustomFlowCompany(companyUUID)) {
        result.description =
            'Thank you for submitting your information. We are processing your request.';
    }

    if (companyUUID === COMPANY_ACCESS_CODES.NEWLINE_AI) {
        result.description = 'Click on the button below to finish your application.';
        result.defaultBtnText = 'Return To Your Application';
        result.defaultText = '';
    }

    return result;
};

export const getAllAttemptsUsedText = (fromMobile, continueAlternateOptions) => {
    if (!fromMobile) {
        return allAttemptsUsedVariants.RETURN_TO_DESKTOP;
    }

    return continueAlternateOptions
        ? allAttemptsUsedVariants.ALTERNATE_VERIFICATION
        : allAttemptsUsedVariants.CONTACT_ADMIN;
};

export const renderEndInstructionBtnText = (instructionType) => {
    switch (instructionType) {
        case INSTRUCTION_TYPES.ID:
            return 'Capture ID';
        case INSTRUCTION_TYPES.SELFIE:
            return 'Take Selfie';
        default:
            return 'Next';
    }
};

export const getSelfieLayoutContent = ({ defaultError, captureError }) => {
    const { CAPTURE_ERROR, DEFAULT_ERROR, TAKE_SELFIE } = SELFIE_FLOW_TYPES;

    const result = {
        type: TAKE_SELFIE,
        title: 'Selfie',
        instructionBtnText: 'Unlock Pro Tips for an Epic Selfie!',
        selfieBtnText: 'Take Selfie Image',
    };

    if (defaultError) {
        result.type = DEFAULT_ERROR;
        result.title = 'Unable to Examine Selfie';
        result.selfieBtnText = 'Retry';
    }

    if (captureError) {
        result.type = CAPTURE_ERROR;
        result.title = 'Selfie Image Capture Failed!';
        result.instructionBtnText = 'How to take a selfie?';
        result.selfieBtnText = 'Please Try Again';
    }

    return result;
};

const getBrowserType = () => {
    const { CHROME, GENERIC, SAFARI, SAMSUNG_INTERNET } = CAMERA_PERMISSION.BROWSERS;

    if (isChrome) return CHROME;

    if (isSamsungBrowser) return SAMSUNG_INTERNET;

    if (isSafari) return SAFARI;

    return GENERIC;
};

const isRunningInWebView = () => {
    try {
        const userAgent = navigator?.userAgent || window?.opera;
        const isAndroidWebView =
            /wv/.test(userAgent) ||
            /; wv\)/.test(userAgent) ||
            window?.ReactNativeWebView !== undefined;
        const isIOSWebView = window?.webkit && window.webkit?.messageHandlers;

        return Boolean(isAndroidWebView || isIOSWebView);
    } catch (error) {}
};

const getBrowserAndPermissions = async () => {
    const { NOT_ENABLED, NOT_ALLOWED, NOT_SUPPORT, UNHANDLED_ERROR } =
        CAMERA_PERMISSION.ERROR_TYPES;

    const browser = getBrowserType();

    const permissions = {
        browser: NOT_SUPPORT, //some browsers not support permissions api or camera property
        os: null,
        isWebView: isRunningInWebView(),
    };

    try {
        const stream = await navigator?.mediaDevices?.getUserMedia({ video: true });
        permissions.os = stream?.active;

        //stop camera after checking the status
        stream?.getTracks()?.forEach((track) => track?.stop());

        stream?.getVideoTracks()[0]?.stop();
    } catch (error) {
        permissions.os = `${error}`?.includes(NOT_ALLOWED) ? NOT_ENABLED : UNHANDLED_ERROR;

        //getUserMedia API may not be supported in browsers released before 2017 or other hardware issues.
        if (permissions.os === UNHANDLED_ERROR) {
            datadogRum.addError(new Error(error), {
                extraValues: {
                    browserInfo: {
                        browserVersion,
                        browserName,
                    },
                    permissionLevels: permissions,
                    deviceInfo: {
                        isAndroid,
                        isIOS,
                        isMacOs,
                    },
                },
            });
        }
    }

    if (isAndroid) {
        try {
            const browser = await navigator.permissions.query({ name: 'camera' });

            permissions.browser = browser?.state;
        } catch (error) {}
    }

    return {
        browser,
        permissions,
    };
};

const getUserDeviceType = () => {
    const { ANDROID, IOS, MACOS } = CAMERA_PERMISSION.DEVICE_OS;

    if (isAndroid) return ANDROID;

    if (isIOS) return IOS;

    if (isMacOs) return MACOS;

    return null;
};

export const getUserDeviceProperties = async () => {
    const { NOT_ENABLED } = CAMERA_PERMISSION.ERROR_TYPES;

    const device = getUserDeviceType();

    const browserAndPermissions = await getBrowserAndPermissions();

    let processedWithCapture = true;

    const permissions = browserAndPermissions.permissions;

    if (['denied'].includes(permissions.browser) || permissions.os === NOT_ENABLED) {
        processedWithCapture = false;
    }

    return {
        device,
        ...browserAndPermissions,
        processedWithCapture,
    };
};

export const getCameraPermissionGuideType = (permissions, device, browserType) => {
    const { ERROR_TYPES, GUIDE_TYPES, BROWSERS, DEVICE_OS } = CAMERA_PERMISSION;

    const { APP, SITE, BOTH } = GUIDE_TYPES;

    const { NOT_ENABLED, NOT_SUPPORT } = ERROR_TYPES;

    const { SAFARI } = BROWSERS;

    const { IOS, MACOS } = DEVICE_OS;

    const { browser, os, isWebView } = permissions ?? {};

    const isAppleDevice = new Set([IOS, MACOS]).has(device);

    if (isWebView) return APP;

    if (isAppleDevice) {
        return browserType === SAFARI ? SITE : APP;
    }

    if (['denied', 'prompt'].includes(browser)) {
        return SITE;
    }

    if (browser === 'granted' && os === NOT_ENABLED) {
        return APP;
    }

    if (browser === NOT_SUPPORT && os === NOT_ENABLED) {
        return BOTH;
    }
};

export const isUserBrowserSupported = () => {
    const browser = getBrowserType();

    const { GENERIC } = CAMERA_PERMISSION.BROWSERS;

    let support = true;

    //This env should only be added in development for testing purposes.
    //Once verified, it can be added to production.
    if (process.env.REACT_APP_CONTROL_UNSUPPORTED_BROWSER === 'true') {
        support = browser !== GENERIC;
    }

    return support;
};

export const getCameraPermissionGuideUsage = ({
    flowType,
    lastUsage,
    verificationType,
    verificationUUID,
}) => {
    const guideUsage = { ...lastUsage };

    const usageCountByVerificationType = (guideUsage?.properties?.[verificationType] ?? 0) + 1;

    return {
        ...lastUsage,
        flow: flowType,
        verificationUUID: verificationUUID,
        properties: {
            ...lastUsage?.properties,
            [verificationType]: usageCountByVerificationType,
        },
    };
};

export const isPageLoadedIframe = () => window?.self !== window?.top;

export const getEnabledExtendedInputs = (extendedInputs) => {
    const keyWithTrueValues = [];
    for (const key in extendedInputs) {
        if (extendedInputs.hasOwnProperty(key) && extendedInputs[key] === true) {
            keyWithTrueValues.push(key);
        }
    }
    return keyWithTrueValues;
};

export const getVerificationSteps = (profile) => {
    const isTwoFaEnabled = profile?.workflow?.pre_identity_checks?.['2fa']?.enabled ?? false;

    if (!isTwoFaEnabled) return [];

    const steps = [
        { id: uuidv4(), label: '2FA' },
        { id: uuidv4(), label: VERIFICATION_TYPES.IDENTITY },
    ];

    const extendedInputs = profile?.extended_checks;
    const extendedChecksList = getEnabledExtendedInputs(extendedInputs);

    extendedChecksList.forEach((check) => {
        steps.push({
            id: uuidv4(),
            label: VERIFICATION_TYPES[check.replaceAll('_', '').toUpperCase()],
        });
    });

    return steps;
};

export const maskUserEmail = (email) => {
    if (!email?.includes('@')) return '';

    const [username, domain] = email.split('@');

    if (!domain.includes('.')) return email;

    const [domainName, ...tldParts] = domain.split('.');
    const tld = '.' + tldParts.join('.');

    //showing literally 20% from each part of the email
    const visibleUsernameChars = Math.max(1, Math.round(username.length * 0.2));
    const visibleDomainChars = Math.max(1, Math.round(domainName.length * 0.2));

    const maskedUsername =
        username.slice(0, visibleUsernameChars) +
        '*'.repeat(username.length - visibleUsernameChars);

    const maskedDomain =
        domainName.slice(0, visibleDomainChars) +
        '*'.repeat(domainName.length - visibleDomainChars);

    return `${maskedUsername}@${maskedDomain}${tld}`;
};

export const maskUserPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber);
    const dialCode = phoneNumberObj ? `+${phoneNumberObj.countryCallingCode}` : null;

    // mask all except last 4 digits
    const maskedNumber = phoneNumberObj?.nationalNumber
        .slice(-4)
        .padStart(phoneNumberObj?.nationalNumber.length, '*');

    return `${dialCode} ${maskedNumber}`;
};

export const getCountryCode = (phoneNumber, country) => {
    if (phoneNumber) {
        const phoneNumberObj = parsePhoneNumberFromString(phoneNumber);
        return phoneNumberObj ? phoneNumberObj?.country : null;
    }

    if (country) {
        return getCountryCodeByName(country);
    }

    return 'us';
};

export const canRequest2faOtp = (session, otpType) => {
    const { EMAIL, PHONE } = TWO_FA_OTP_TYPE;

    let requestOtp = false;

    const { email, phone } = session?.leftOtpAttempts ?? {};

    const isEmailEligible = otpType === EMAIL && email > 0;

    const isPhoneEligible = otpType === PHONE && phone > 0;

    if (isEmailEligible || isPhoneEligible) {
        requestOtp = true;
    }

    return requestOtp;
};

export const extract2faErrorScreenInfo = ({ title, message, btnText, metadata = {} }) => {
    const result = {
        title,
        message,
        btnText,
        isBtnEnabled: Boolean(btnText),
    };

    const isSessionExpiredScreen = title === 'Session Expired';

    if (isSessionExpiredScreen) {
        const { twoFaSessionExpired, twoFaAttemptExhausted } = metadata;

        const canReInitiate2faSession = twoFaSessionExpired && !twoFaAttemptExhausted;

        result.isBtnEnabled = canReInitiate2faSession;
        result.message = canReInitiate2faSession ? message : TWO_FA_ERRORS.SESSION_EXPIRED;
    }

    return result;
};

export const convertToMMDDYYYY = (dateString) => {
    if (dateString) {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return dateString;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}-${day}-${year}`;
    }
};

const validateDatePattern = (dateStr) => {
    const pattern = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;
    return pattern.test(dateStr) ? null : 'Invalid date.';
};

export const validateDateOfBirth = (dob) => {
    if (!dob) {
        return null;
    }

    const dobPatternError = validateDatePattern(dob);

    if (dobPatternError) {
        return 'Invalid Date.';
    }

    const age = getDobAgeYears(dob);

    if (age === null) {
        return 'Invalid Date.';
    }

    if (age < 18) {
        return 'Selection beyond current date is disabled.';
    }

    return null;
};

export const getDateBeforeEighteenYears = () => dateEighteenYearsAgo();

export const getWeekdayFirstLetter = (locale, date) => {
    const options = { weekday: 'short' };
    const weekday = new Intl.DateTimeFormat(locale, options).format(date);
    return weekday.charAt(0);
};

//contact is an international phone number, eg 918xxxxx8888
export const formatContactNumber = (contact) => {
    if (!contact) return;

    //important for parsing international phone numbers
    const modifiedContact = contact[0] === '+' ? contact : `+${contact}`;

    const parsedContact = parsePhoneNumberFromString(modifiedContact);
    const formattedContact = parsedContact?.formatInternational();

    return formattedContact;
};

export const getStates = (countryInput) => {
    if (!countryInput) return [];

    // if country name is provided, then convert it to country code
    if (typeof countryInput === 'string' && countryInput.length > 3) {
        countryInput = getCountryCodeByName(countryInput);
    }

    const input = countryInput.trim().toUpperCase();
    let countryCode = '';

    if (input.length === 3) {
        // remove the last character
        // because the package we are using recognized only two character country code
        countryCode = input.slice(0, 2);
    } else if (input.length === 2) {
        countryCode = input;
    } else {
        countryCode = getCountryCodeByName(input);
    }

    if (!countryCode) return [];

    const states = State.getStatesOfCountry(countryCode).map((state) => ({
        label: state.name,
        value: state.isoCode,
    }));

    return states;
};

export const convertImageDataToBlob = async (imageData) => {
    try {
        if (!imageData?.width || !imageData?.height || !imageData?.data) {
            throw new Error('Invalid imageData provided to convertImageDataToBlob');
        }

        // Create an off-screen canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas dimensions
        canvas.width = imageData.width;
        canvas.height = imageData.height;

        if (!ctx) {
            throw new Error('Failed to get 2d context from canvas');
        }

        // Put the image data onto the canvas
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to blob
        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to convert canvas to blob'));
                }
            }, 'image/png');
        });

        return blob;
    } catch (error) {
        datadogRum.addError(new Error('Error in convertImageDataToBlob'), {
            error,
            imageDataType: typeof imageData,
            hasImageData: !!imageData,
            width: imageData?.width,
            height: imageData?.height,
        });

        throw error;
    }
};

export const getUserFullname = (firstname, middlename, lastname) => {
    return [firstname, middlename, lastname].filter(Boolean).join(' ').trim();
};

export const areAllAddressFieldsPresent = ({ userDetails, company, mandatoryFields }) => {
    const isAddressBlocked = isSSNOrAddressFieldsBlockedForCustomer(company?.uuid);

    if (isAddressBlocked && !mandatoryFields.includes('address')) {
        return true;
    }

    const addressFields = [
        'house_number',
        'street_name',
        'city',
        'zip_code',
        'state',
        'country',
        'address',
    ];

    return addressFields.every((field) => {
        return (
            userDetails[field] !== undefined &&
            userDetails[field] !== null &&
            userDetails[field]?.trim() !== ''
        );
    });
};

export const checkIfMandatoryFieldsAreLeft = (
    userDetails = {},
    mandatoryFields = [],
    company = null
) => {
    const { first_name, middle_name, last_name, date_of_birth, email, phone } = userDetails;

    const fullName = getUserFullname(first_name, middle_name, last_name);
    const isAddressValid = areAllAddressFieldsPresent({ userDetails, company, mandatoryFields });

    const userValues = {
        name: fullName,
        dob: date_of_birth,
        email,
        phone_number: phone,
        address: isAddressValid ? 'valid' : '',
    };

    const missingFields = mandatoryFields.filter((field) => {
        const value = userValues[field];
        return (
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '')
        );
    });

    return missingFields.length === 0;
};

// passport reports its status via `authentication_result` ('Passed'/'Failed'),
// scannedUser via `result` ('complete'/'failed'). Treat either as complete.
const isDocumentResultComplete = (doc = {}) =>
    doc?.result === 'complete' || doc?.authentication_result === 'Passed';

/**
 * Choose which verifyUI document (passport vs scannedUser) to trust for the
 * user's identity fields.
 *
 * Rules (bug fix): never surface data from a document whose Result failed while
 * the other document is usable. Prefer the document whose Result is complete;
 * if neither is complete, fall back to the most recently uploaded document
 * (`uploaded_at`). When both are complete, keep the historical passport-first
 * preference.
 *
 * Returns identity fields normalized to camelCase form keys, since passport and
 * scannedUser use different field names (firstname vs first_name, etc.).
 *
 * @param {object} verifyUI - the getTestResult `verifyUI` object
 */
export const selectPreferredIdDocument = (verifyUI = {}) => {
    const passport = verifyUI?.passport || {};
    const scanned = verifyUI?.scannedUser || {};

    const passportComplete = isDocumentResultComplete(passport);
    const scannedComplete = isDocumentResultComplete(scanned);

    let chosen;
    if (passportComplete && !scannedComplete) {
        chosen = passport;
    } else if (scannedComplete && !passportComplete) {
        chosen = scanned;
    } else if (passportComplete && scannedComplete) {
        chosen = passport; // both complete → existing passport-first preference
    } else {
        // neither complete → prefer the most recently uploaded document
        const passportTime = new Date(passport?.uploaded_at || 0).getTime();
        const scannedTime = new Date(scanned?.uploaded_at || 0).getTime();
        chosen = scannedTime > passportTime ? scanned : passport;
    }

    const isPassport = chosen === passport;
    return {
        firstName: isPassport ? chosen?.firstname : chosen?.first_name,
        middleName: isPassport ? chosen?.middlename : chosen?.middle_name,
        lastName: isPassport ? chosen?.lastname : chosen?.last_name,
        dob: chosen?.dob,
    };
};

export const verifyUserIdentity = (userData, resultData) => {
    const {
        first_name: userFirst,
        middle_name: userMiddle,
        last_name: userLast,
        date_of_birth: userDob,
    } = userData || {};

    const {
        firstName: idFirst,
        middleName: idMiddle,
        lastName: idLast,
        dob: idDob,
    } = selectPreferredIdDocument(resultData);

    const userFullName = getUserFullname(userFirst, userMiddle, userLast).toLowerCase();
    const idFullName = getUserFullname(idFirst, idMiddle, idLast).toLowerCase();

    const isCorrectUserDetails = userFullName === idFullName && userDob === idDob;

    return isCorrectUserDetails;
};

export const getAuthDBVerificationDecision = (response) => {
    const { success } = response || {};
    const { ssn } = response?.IDVScore || {};

    // if success and ssn is null or ssn is greater than 99 then, we will consider it as pass
    if (success && (ssn === null || ssn >= 99)) {
        return AUTH_DB_VERIFICATION_DECISION.PASS;
    }

    // If the success is false then, we will consider it as ask for fallback
    return AUTH_DB_VERIFICATION_DECISION.ASK_FOR_FALLBACK;
};

// check if user's name and dob exactly matches the dummy PII object
export const isDummyUserPII = (userDetails) => {
    return Object.entries(fakeUserInfo).every(([key, fakeValue]) => userDetails[key] === fakeValue);
};

export const isUSA = (country) =>
    COUNTRY_USA_VALIDATION_VARIANTS.includes((country || '').toLowerCase());

export const isProdEnv = () => ['prod', 'production'].includes(process.env?.REACT_APP_ENV);

// Escape regex metacharacters so user-typed input can be safely used inside a RegExp
export const escapeRegExp = (str = '') => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const highlightMatch = (label, query) => {
    if (!query) return label;

    let parts;
    try {
        const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
        parts = label.split(regex);
    } catch {
        // If the pattern is somehow still invalid, fall back to the plain label
        return label;
    }

    return (
        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {parts.map((part, index) =>
                part.toLowerCase() === query.toLowerCase() ? (
                    <span key={part + index} style={{ fontWeight: 'bold' }}>
                        {part}
                    </span>
                ) : (
                    part
                )
            )}
        </span>
    );
};

export const getCountryNameByCode = (code) => {
    if (code === 'USA') {
        return 'United States of America';
    }

    return Country.getCountryByCode(code)?.name || code;
};

export const getStateNameByCode = (stateCode, countryCode) => {
    if (!countryCode) return stateCode;
    if (COUNTRY_USA_VALIDATION_VARIANTS.includes(countryCode.toLowerCase())) {
        // Use local mapping for US states
        const found = states.find((s) => s.value === stateCode || s.label === stateCode);
        return found ? found.label : stateCode;
    }

    // if instead of country code we are getting country name, then convert it to country code
    if (countryCode.length > 3) {
        countryCode = getCountryCodeByName(countryCode);
    }

    const state = State.getStateByCode(stateCode, countryCode);

    return state?.name || stateCode;
};

export const isSSNOrAddressFieldsBlockedForCustomer = (companyUUID) => {
    return SSN_AND_ADDRESS_FIELDS_BLOCKED_CUSTOMERS.includes(companyUUID);
};

export const checkIfAddressFieldIsBlocked = ({
    companyUUID,
    mandatoryFields,
    isRiskAssessmentRoute,
    userDetails,
}) => {
    const companyBlockedSsnOrAddress = isSSNOrAddressFieldsBlockedForCustomer(companyUUID);

    const isAddressMandatory = mandatoryFields.includes('address');

    const result = {
        ssnBlocked: companyBlockedSsnOrAddress,
        addressBlocked: companyBlockedSsnOrAddress, //include country, state, city, house number, street name and address fields
        zipCodeBlocked: companyBlockedSsnOrAddress,
    };

    // if the user is on the risk assessment route, and the zip code is not provided, then the zip code is not blocked
    if (isRiskAssessmentRoute && companyBlockedSsnOrAddress && !userDetails?.zip_code) {
        result.zipCodeBlocked = false;
    }

    // if the address is mandatory, then the address is not blocked
    if (isAddressMandatory) {
        result.addressBlocked = false;
        result.zipCodeBlocked = false;
    }

    return result;
};

export const isValidName = (value) => {
    const allowedRegex = /^[\p{L}\p{M} ._\-'’]+$/u;
    const invisibleRegex = /\p{Cf}/u;
    const controlRegex = /\p{Cc}/u;

    if (value) {
        const normalized = value.normalize('NFC');

        if (invisibleRegex.test(normalized) || controlRegex.test(normalized)) {
            return false;
        }

        return allowedRegex.test(normalized);
    } else {
        return true;
    }
};

export function isValidPhoneNumber(inputNumber) {
    // allow empty input, yup will handle required separately
    if (!inputNumber || inputNumber?.trim() === '') {
        return true;
    }
    // Normalize: add '+' if missing and input starts with country code digits
    const normalizedNumber = inputNumber.startsWith('+') ? inputNumber : `+${inputNumber}`;
    const phoneNumber = parsePhoneNumberFromString(normalizedNumber);

    if (!phoneNumber) {
        return false;
    }
    return phoneNumber.isValid();
}

export const canReturnToCompany = (profile, verificationConfiguration) => {
    const redirectUrl = profile?.company?.redirect_url;

    if (!redirectUrl) return false;

    const verificationDetails = profile?.verification_details ?? {};

    const { isIdentityVerificationCompleted, isIncompleteWithExhaustedAttempts } =
        isVerificationCompleted({
            extendedInputs: profile?.extended_checks,
            kbaVerified: verificationDetails?.is_kba_verified === 1,
            bankVerified: Boolean(
                verificationDetails?.is_bank_verified || verificationDetails?.is_bank_owner_matched
            ),
            documentVerified: verificationDetails?.is_document_valid,
            selfieAttempted: (verificationDetails?.selfie_attempts ?? 0) > 0,
            workflowIdVerifications: profile?.workflow?.id_verifications,
            verificationDetails,
            verificationConfiguration,
            quickVerifyPassed: verificationDetails?.is_quick_verify_passed,
        });

    return isIdentityVerificationCompleted || isIncompleteWithExhaustedAttempts;
};

export const handleUserRedirect = (redirectUrl) => {
    const url = typeof redirectUrl === 'string' ? redirectUrl : redirectUrl?.toString?.() ?? '';

    if (url.length > 0) {
        const isHttpUrl = url.startsWith('http://') || url.startsWith('https://');
        const hasCustomScheme = url.includes('://');
        const targetUrl = isHttpUrl || hasCustomScheme ? url : `//${url}`;

        window.parent.location.href = targetUrl;
    }
    sessionStorage.clear();
};

/**
 * Returns the descriptive text and reversal-button visibility for the "no device" banner.
 *
 * @param {object}  params
 * @param {boolean} params.govtIdAvailable         GOVT_ID is enabled and has attempts (ignoring noDevice)
 * @param {boolean} params.fallbackAvailable       At least one fallback (KBA/bank) is enabled with attempts left
 * @param {boolean} params.fallbackConfigured      At least one fallback is configured in the workflow
 * @param {string}  params.verificationConfiguration  'OR' | 'AND' | 'SINGLE'
 * @returns {{ text: string, showDeviceReturnButton: boolean }}
 */
export const getNoDeviceContent = ({ govtIdAvailable, fallbackAvailable, fallbackConfigured }) => {
    let text;

    if (govtIdAvailable && fallbackAvailable) {
        text =
            "Since you don't have a valid government ID, choose one of the verification options below.";
    } else if (!govtIdAvailable && fallbackAvailable) {
        text = 'Verify your identity using one of the options below.';
    } else if (govtIdAvailable && !fallbackConfigured) {
        text =
            'A government ID or passport is required to continue verification. Please contact your administrator for help.';
    } else if (govtIdAvailable && fallbackConfigured) {
        text =
            'No alternate verification options are currently available. Please contact your administrator for help.';
    } else {
        text =
            'No verification methods are currently available. Please contact your administrator for help.';
    }

    return { text, showDeviceReturnButton: govtIdAvailable };
};

// Result component utils (src/components/Result/Result.jsx)

// Maps nested field keys to their proper labels (e.g. 'score' → 'Liveness Score') derived from
// ORDERED_FIELDS so the overflow fallback uses the same labels as the structured path would.
// Maps nested field keys to their { label, noCase } config, derived from ORDERED_FIELDS so the
// overflow fallback formats and labels nested data the same way the structured path would.
const NESTED_KEY_FIELD_MAP = ORDERED_FIELDS.reduce((map, fieldDef) => {
    if (fieldDef.nestedParent) {
        for (const key of fieldDef.keys) {
            map[key] = { label: fieldDef.label, noCase: fieldDef.noCase };
        }
    }
    return map;
}, {});

// Certain keys (e.g. 'passportNumber', 'license') have document-specific labels in DYNAMIC_LABEL_MAP;
// all others fall back to the default label from ORDERED_FIELDS.
function getFieldLabel(matchedKey, defaultLabel) {
    return DYNAMIC_LABEL_MAP[matchedKey] ?? defaultLabel;
}

// Converts a raw field value to a display string.
// noCase skips startCase formatting (used for dates, IDs, codes); 'verifyUI' maps to 'Medallion™'.
function formatValue(val, noCase = false) {
    const str = val == null ? '' : String(val);
    if (!str) return '';
    if (str === 'verifyUI') return 'Medallion™';
    return noCase ? str : startCase(str);
}

// Looks up a field value using ORDERED_FIELDS config, trying each key alias in order.
// For nested fields (e.g. liveness), probes LivenessResult then livenessResult then the parent
// directly to handle both PascalCase and camelCase API response shapes.
function resolveFieldValue(object, fieldDef) {
    const { keys, nestedParent } = fieldDef;
    const nestedObject =
        object[nestedParent]?.LivenessResult ??
        object[nestedParent]?.livenessResult ??
        object[nestedParent] ??
        {};
    const source = nestedParent ? nestedObject : object;
    for (const key of keys) {
        const val = source[key];
        if (val !== undefined && val !== null && val !== '') {
            return { matchedKey: key, value: val };
        }
    }
    return null;
}

//builds rows for all ORDERED_FIELDS matches and tracks which top-level keys were consumed.
function buildOrderedRows(object) {
    const consumedKeys = new Set();
    const rows = [];
    for (const fieldDef of ORDERED_FIELDS) {
        const resolved = resolveFieldValue(object, fieldDef);
        if (!resolved) continue;
        const { matchedKey, value } = resolved;
        const label = getFieldLabel(matchedKey, fieldDef.label);
        const displayValue = formatValue(value, fieldDef.noCase);
        if (!displayValue) continue;
        rows.push({ key: `ordered-${matchedKey}`, label, value: displayValue });
        // Track parent key for nested fields, matched key for top-level fields.
        consumedKeys.add(fieldDef.nestedParent ?? matchedKey);
    }
    return { rows, consumedKeys };
}

// Flattens one level of a nested object into display rows.
// Handles liveness data regardless of the exact parent key name the API uses.
function flattenNestedObject(parentKey, nestedObj) {
    const rows = [];
    for (const [nestedKey, nestedVal] of Object.entries(nestedObj)) {
        if (REMOVED_KEYS.has(nestedKey)) continue;
        if (!nestedVal || typeof nestedVal === 'object') continue;
        const fieldDef = NESTED_KEY_FIELD_MAP[nestedKey];
        const value = formatValue(nestedVal, fieldDef?.noCase);
        if (!value) continue;
        rows.push({
            key: `overflow-${parentKey}-${nestedKey}`,
            label: fieldDef?.label ?? startCase(nestedKey),
            value,
        });
    }
    return rows;
}

// Nested objects are flattened one level deep; primitives are rendered directly.
function buildOverflowRows(object, consumedKeys) {
    const rows = [];
    for (const [key, val] of Object.entries(object)) {
        if (REMOVED_KEYS.has(key) || consumedKeys.has(key) || !val) continue;
        if (typeof val === 'object') {
            rows.push(...flattenNestedObject(key, val));
            consumedKeys.add(key);
            continue;
        }
        const str = String(val);
        if (!str) continue;
        rows.push({ key: `overflow-${key}`, label: startCase(key), value: str });
    }
    return rows;
}

// Builds the full ordered + overflow row list for a document object.
export function buildDisplayRows(object) {
    const { rows: orderedRows, consumedKeys } = buildOrderedRows(object);
    return [...orderedRows, ...buildOverflowRows(object, consumedKeys)];
}

// Derive the verification mode from the workflow's id_verifications:
// quick_verify is excluded; all enabled methods mandatory => 'AND', a mix of
// mandatory/optional => 'OR', a single enabled method => 'SINGLE'. Returns
// undefined when nothing is enabled.
export const getVerificationConfiguration = (idVerification) => {
    const enabledVerifications = [];
    const mandatoryVerifications = [];

    for (const key in idVerification) {
        const check = idVerification[key];

        if (check.enabled && key !== QUICK_VERIFY_PROPERTY.quick_verify) {
            enabledVerifications.push(check.verification_type);
            if (check.mandatory) {
                mandatoryVerifications.push(check.verification_type);
            }
        }
    }

    const allOrAnyMethod =
        mandatoryVerifications.length === enabledVerifications.length ? 'AND' : 'OR';

    return enabledVerifications.length === 1 ? 'SINGLE' : allOrAnyMethod;
};
